import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@chakra-ui/react";
import StandardModal from "@/js/components/shared/StandardModalV2";
import NuSingleSelect from "@/js/components/NuSingleSelect";
import "bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { getUserDisplayTextForSelect } from "@/js/utils/userUtils";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { getBhsHistory } from "@/js/api/bhsApi";
import SHDateRangePicker from "./components/SHDateRangePicker";
import SHViewModal from "./components/SHViewModal";
import { isScreenshareUrl } from "@/components/Screenshare/util";

const ScreenshotHistoryModal = ({ students, handleClose }) => {
	const { t } = useTranslation();
	const SearchState = {
		Initial: "Initial",
		Ready: "Ready",
		Waiting: "Waiting",
		ShowingResults: "ShowingResults",
		ServerError: "ServerError",
	};

	const CollapseState = {
		Initial: "Initial", // collapsed and never expanded
		Collapsed: "Collapsed",
		Expanded: "Expanded",
	};

	const [availableStudents, setAvailableStudents] = useState([]);
	const [selectedStudent, setSelectedStudent] = useState("");
	const [studentHistory, setStudentHistory] = useState(null);
	const [startDate, setStartDate] = useState(moment().startOf("day"));
	const [endDate, setEndDate] = useState(moment().endOf("day"));
	const [searchState, setSearchState] = useState(SearchState.Initial);
	const [collapseState, setCollapseState] = useState({});
	const [sortId, setSortId] = useState("date");
	const [sortDirection, setSortDirection] = useState("desc");
	const [dateDirection, setDateDirection] = useState("desc");
	const [sortDirectionChanged, setSortDirectionChanged] = useState(false);
	const [viewingHistory, setViewingHistory] = useState(false);
	const [currentActivity, setCurrentActivity] = useState(null);

	useEffect(() => {
		setAvailableStudents(
			students
				.map((student) => {
					student.fname = student.fname || "";
					student.lname = student.lname || "";
					return student;
				})
				.sort((a, b) => a.username.localeCompare(b.username))
				.map((student) => ({
					text: getUserDisplayTextForSelect(student),
					value: student.username,
				}))
		);
	}, [students]);

	const handleStudentChange = (student) => {
		setSelectedStudent(student);
		onSearchParametersChanged({ student });
	};

	const handleDateChangeCallback = (start, end) => {
		setStartDate(start);
		setEndDate(end);
		onSearchParametersChanged({ start, end });
	};

	const onSearchParametersChanged = ({ student = selectedStudent, start = startDate, end = endDate }) => {
		if (student !== "" && start !== null && end !== null && searchState === SearchState.Initial) {
			setSearchState(SearchState.Ready);
		} else if (student === "" && start === null && end === null) {
			setSearchState(SearchState.Initial);
		}
	};

	const handleSearchButtonClick = async () => {
		setSearchState(SearchState.Waiting);
		getBhsHistory(selectedStudent, startDate, endDate).then((result) => {
			if (result === null) {
				setStudentHistory(null);
				setSearchState(SearchState.ServerError);
			} else {
				const studentHistoryObject = {
					email: selectedStudent,
					activities: [],
				};

				// create a bucket of activities
				for (let i = 0; i < result.length; i++) {
					const activity = result[i];
					// When the agent sends activity without active url
					if (activity.activity === "") {
						activity.activity = t("screenshotHistory.nonBrowsingActivity");
					}
					// When the agent sends default page of browser
					activity.activity = activity.activity.replace("<blank>", t("screenshotHistory.browserHomepage"));

					activity.start = moment.utc(activity.start).subtract(1, "seconds").local();
					activity.end = moment.utc(activity.end).local();
					studentHistoryObject.activities.push(activity);
				}
				setStudentHistory(studentHistoryObject);

				// sort browsing history events by date and time in descending order
				studentHistoryObject.activities.sort((activity1, activity2) => (activity1.start.isBefore(activity2.start) ? 1 : -1));

				// sort browsing history into sublists by day.
				// resulting map will be sorted by date in descending order.
				const activitiesByDate = new Map();
				for (const activity of studentHistoryObject.activities) {
					const dateKey = activity.start.format("MMMM D, YYYY");
					if (!activitiesByDate.has(dateKey)) {
						activitiesByDate.set(dateKey, []);
					}
					//sorting screenshots of activities in decending order of date
					activity.screenshots.sort((activity1, activity2) => (activity1.timestamp <= activity2.timestamp ? 1 : -1));
					activitiesByDate.get(dateKey).push(activity);
				}

				// now we have a map of date buckets ordered by descending date
				// with each date bucket being an array with browsing history
				// events sorted by descending order.
				studentHistoryObject.activitiesByDate = activitiesByDate;

				setStudentHistory(studentHistoryObject);

				if (studentHistoryObject.activitiesByDate.size === 1) {
					// if there is only one day in the history, start with an expanded view
					const newCollapseState = {};
					newCollapseState[studentHistoryObject.activitiesByDate.keys().next().value] = CollapseState.Expanded;
					setCollapseState(newCollapseState);
				} else {
					// otherwise start with a collapsed view
					setCollapseState({});
				}
				setSearchState(SearchState.ShowingResults);
			}
		});
	};

	const handleSeparatorClick = (e) => {
		const id = e.target.dataset.date;
		const newCollapseState = Object.assign({}, collapseState);
		newCollapseState[id] = collapseState[id] === CollapseState.Expanded ? CollapseState.Collapsed : CollapseState.Expanded;
		setCollapseState(newCollapseState);
	};

	const handleColumnHeaderClick = (e) => {
		if (studentHistory !== null) {
			const newSortId = e.target.dataset.sortid;
			let newSortDirection;
			if (newSortId === sortId) {
				newSortDirection = sortDirection === "asc" ? "desc" : "asc";
			} else {
				newSortDirection = "asc";
			}
			setSortId(newSortId);
			setSortDirection(newSortDirection);
			setSortDirectionChanged(true);

			// adjust the order of the date entries in the map if sorted by date
			if (newSortId === "date" && dateDirection !== newSortDirection) {
				studentHistory.activitiesByDate = new Map(Array.from(studentHistory.activitiesByDate).reverse());
				setDateDirection(newSortDirection);
			}

			for (const activities of studentHistory.activitiesByDate.values()) {
				// sort map entries by selected order
				activities.sort((activity1, activity2) => {
					switch (newSortId) {
						case "date":
							return (newSortDirection === "asc" ? 1 : -1) * (activity1.start.isBefore(activity2.start) ? -1 : 1);

						case "activity":
							return (newSortDirection === "asc" ? 1 : -1) * activity1.activity.localeCompare(activity2.activity);

						case "duration":
							return (
								(newSortDirection === "asc" ? 1 : -1) *
								(moment.duration(activity1.end.diff(activity1.start)).asSeconds() -
									moment.duration(activity2.end.diff(activity2.start)).asSeconds())
							);

						default:
							console.log("unexpected sort column: " + newSortId);
							return 0;
					}
				});
			}
		}
	};

	const handleViewClick = (e) => {
		const dateKey = e.target.dataset.dateKey;
		const activityIndex = e.target.dataset.activityIndex;
		const activity = studentHistory.activitiesByDate.get(dateKey)[activityIndex];

		setCurrentActivity(activity);
		setViewingHistory(true);
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const toggleViewModal = (event) => {
		setViewingHistory(!viewingHistory);
	};

	const renderHistory = () => {
		const jsx = [];
		for (const dateKey of studentHistory.activitiesByDate.keys()) {
			if (collapseState[dateKey] === undefined) {
				collapseState[dateKey] = CollapseState.Initial;
			}
			const separatorJsx = (
				<div
					className={`SHModal_tableRow SHModal_tableSeparatorRow clickable ${collapseState[dateKey]}`}
					key={`separator_${dateKey}`}
					data-date={dateKey}
					onClick={handleSeparatorClick}
				>
					{dateKey}
				</div>
			);
			jsx.push(separatorJsx);

			const linesOfDate = [];
			for (const [i, activity] of studentHistory.activitiesByDate.get(dateKey).entries()) {
				var durationSeconds = Math.floor(moment.duration(activity.end.diff(activity.start)).asSeconds());
				durationSeconds = durationSeconds === 0 ? 10 : durationSeconds;
				const durationMinutes = Math.floor(durationSeconds / 60);
				const durationString =
					durationSeconds >= 60
						? t("screenshotHistory.durationMinutesShort", { count: durationMinutes })
						: t("screenshotHistory.durationSecondsShort", { count: durationSeconds });
				const isScreenshare = isScreenshareUrl(activity.activity);

				const lineJsx = (
					<div className="SHModal_tableRow SHModal_tableContentRow" key={i}>
						<div className="SHModal_tableCell SHModal_tableContentCell">{activity.start.format("hh:mm:ss A")}</div>
						<div className="SHModal_tableCell SHModal_tableContentCell">
							<div>{isScreenshare ? t("screenshotHistory.teachersSharedScreen") : activity.activity}</div>
						</div>
						<div className="SHModal_tableCell SHModal_tableContentCell">{durationString}</div>
						<div
							className="SHModal_tableCell SHModal_tableContentCell clickable"
							data-date-key={dateKey}
							data-activity-index={i}
							onClick={handleViewClick}
						>
							{t("screenshotHistory.viewLink")}
						</div>
					</div>
				);
				linesOfDate.push(lineJsx);
			}

			jsx.push(
				<div className="SHModal_collapsibleBlock" key={`block_${dateKey}`}>
					{linesOfDate}
				</div>
			);
		}

		return (
			<Fragment>
				<div className="SHModal_tableContent">{jsx}</div>
			</Fragment>
		);
	};

	return (
		<Fragment>
			<StandardModal
				open
				close={handleClose}
				size="content"
				contentClassName="SJRModal"
				rootClassName="SJRModal_wrapper"
				ariaHideApp={false}
			>
				<div className="SHModal_header" data-testid="shr-modal">
					<Box display="flex" justifyContent="left" marginBottom={2}>
						<h2>{t("screenshotHistory.modalTitle")}</h2>
					</Box>
					{t("screenshotHistory.intro")}
				</div>

				<div className="SHModal_searchBar">
					<NuSingleSelect
						allItems={availableStudents}
						noSearchIcon
						searchPlaceholder={t("screenshotHistory.selectStudentPlaceholder")}
						onChange={handleStudentChange}
					/>
					<SHDateRangePicker
						className="SHModal_shDateRangePicker"
						startDate={moment().startOf("day")}
						endDate={moment().endOf("day")}
						onCallback={handleDateChangeCallback}
					/>

					<button
						disabled={searchState === SearchState.Initial}
						onClick={handleSearchButtonClick}
						className={`SHModal_searchButton ${searchState === SearchState.Waiting && "SHModal_searchButtonSpinner"}`}
					>
						{searchState === SearchState.Waiting ? t("screenshotHistory.searching") : t("screenshotHistory.search")}
					</button>
				</div>

				<div className="SHModal_table">
					<div className="SHModal_tableRow SHModal_tableHeaderRow">
						<div
							className={`SHModal_tableCell SHModal_tableHeaderCell clickable ${
								searchState === SearchState.ShowingResults && sortDirectionChanged && sortId === "date" && sortDirection
							}`}
							data-sortid="date"
							onClick={handleColumnHeaderClick}
						>
							<span>{t("screenshotHistory.columnDate")}</span>
							<span
								className={`SHModal_columnSortButton ${
									searchState === SearchState.ShowingResults && sortDirectionChanged && sortId === "date" && sortDirection
								}`}
							></span>
						</div>
						<div className="SHModal_tableCell SHModal_tableHeaderCell clickable" data-sortid="activity" onClick={handleColumnHeaderClick}>
							<span>{t("screenshotHistory.columnActivity")}</span>
							<span
								className={`SHModal_columnSortButton ${
									searchState === SearchState.ShowingResults && sortDirectionChanged && sortId === "activity" && sortDirection
								}`}
							></span>
						</div>
						<div
							className={`SHModal_tableCell SHModal_tableHeaderCell clickable ${
								searchState === SearchState.ShowingResults && sortDirectionChanged && sortId === "duration" && sortDirection
							}`}
							data-sortid="duration"
							onClick={handleColumnHeaderClick}
						>
							<span>{t("screenshotHistory.columnDuration")}</span>
							<span
								className={`SHModal_columnSortButton ${
									searchState === SearchState.ShowingResults && sortDirectionChanged && sortId === "duration" && sortDirection
								}`}
							></span>
						</div>
					</div>
					<div className="SHModal_tableRow SHModal_tableFilterRow">
						{searchState === SearchState.ShowingResults && (
							<Fragment>
								<div className="SHModal_tableCell SHModal_tableFilterCell"></div>
								<div className="SHModal_tableCell SHModal_tableFilterCell">
									<NuSingleSelect
										allItems={[]}
										noSearchIcon
										searchPlaceholder={t("screenshotHistory.filterPlaceholder")}
										// onChange={}
									/>
								</div>
								<div className="SHModal_tableCell SHModal_tableFilterCell"></div>
							</Fragment>
						)}
					</div>
					{(searchState === SearchState.Initial || searchState === SearchState.Ready) && (
						<div className="SHModal_instructions">{t("screenshotHistory.instructions")}</div>
					)}
					{searchState === SearchState.ShowingResults && studentHistory !== null && studentHistory.activitiesByDate.size === 0 && (
						<div className="SHModal_noResults">
							<h3>{t("screenshotHistory.noResultsTitle")}</h3>
							<p>{t("screenshotHistory.noResultsBody")}</p>
						</div>
					)}
					{searchState === SearchState.ServerError && (
						<div className="SHModal_serverError">
							<h3>{t("screenshotHistory.serverErrorTitle")}</h3>
							<p>{t("screenshotHistory.serverErrorBody")}</p>
						</div>
					)}
					{searchState === SearchState.ShowingResults && renderHistory()}
				</div>
			</StandardModal>
			{viewingHistory && <SHViewModal handleClose={toggleViewModal} student={selectedStudent} activity={currentActivity} />}
		</Fragment>
	);
};

ScreenshotHistoryModal.propTypes = {
	handleClose: PropTypes.func.isRequired,
	students: PropTypes.array,
};

export default ScreenshotHistoryModal;
